// Trigger sets — authored per report; see docs/builder-report-authoring-spec.md.
import type { TriggerQuestion } from "./types";
import { PLANT_PEDESTRIAN, MANUAL_HANDLING, SLIPS_TRIPS } from "./shared";

const DUMPER_OVERTURN = "haz-plant-equipment-site-dumper-overturn-on-slope-or-tipping-edge";
const OPERATOR_THROWN = "haz-plant-equipment-operator-thrown-from-plant-in-overturn";
const LOAD_LOSS = "haz-plant-equipment-load-loss-or-instability-from-overloading";
const RUN_AWAY = "haz-plant-equipment-plant-run-away-on-gradient";
const REFUELLING_FIRE = "haz-plant-equipment-fire-or-burns-during-plant-refuelling";

const VEHICLE_COLLISION = "haz-plant-equipment-vehicle-to-vehicle-collision-at-junction-or-shared-area";
const REVERSING_STRIKE = "haz-plant-equipment-person-struck-during-vehicle-reversing";
const GATE_DELIVERIES = "haz-plant-equipment-uncontrolled-vehicles-and-deliveries-at-site-gate";
const BOUNDARY_PUBLIC = "haz-public-interface-pedestrian-struck-at-site-boundary";
const ROUTES_DEGRADING = "haz-plant-equipment-traffic-routes-degrading-as-site-changes";

const MSK_HEAVY = "haz-manual-handling-musculoskeletal-injury-from-heavy-load";
const POSTURE_STRAIN = "haz-manual-handling-strain-from-awkward-posture-or-repetitive-handling";
const CUT_CRUSH = "haz-manual-handling-cut-crush-or-puncture-injury-from-load";
const TEAM_LIFT = "haz-manual-handling-team-lift-coordination-failure";

export const SITE_PLANT_TRIGGERS: Record<string, TriggerQuestion[]> = {
  "site-dumper": [
    {
      key: "operatorCard",
      label: "Operator competence — CPCS/NPORS card number for the named dumper operator",
      kind: "text",
      placeholder: "e.g. CPCS 12345678, forward-tipping dumper category",
      requiredCheck: "Dumper operator card recorded",
      effects: { "*": { scopeLine: "Authorised dumper operator: {answer}." } },
    },
    {
      key: "tippingAtEdge",
      label: "Will the dumper tip into an excavation or over an open edge?",
      kind: "yesno",
      effects: {
        yes: {
          scopeLine: "Tipping into an excavation / over an open edge — stop blocks set back from the edge to allow for the skip's tipping reach, dumper reversed square to the edge with a banksman.",
          requireFields: ["nearbyTradesOrPublic"],
        },
        no: {
          permitsRemove: ["Permit / sign-off for any tipping point next to an excavation or open edge"],
          emergencyOff: ["Tipping-edge or excavation incident"],
          scopeLine: "No tipping into an excavation or over an open edge — material is tipped at a prepared stockpile on firm level ground.",
        },
      },
      notSure: {
        failCheck: "Tipping-edge arrangements confirmed",
      },
    },
    {
      key: "gradients",
      label: "Are haul-route gradients checked against the dumper's stated limits?",
      kind: "yesno",
      effects: {
        yes: {
          scopeLine: "Haul-route gradients assessed against the machine's stated limits; routes within limit and an alternative used where they are not.",
        },
        no: {
          failCheck: "Haul-route gradients confirmed against the machine's limits",
        },
      },
      notSure: { hazardsOn: [RUN_AWAY], failCheck: "Haul-route gradients confirmed against the machine's limits" },
    },
    {
      key: "pedestrians",
      label: "Are pedestrians or other trades working in the areas the dumper operates?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [PLANT_PEDESTRIAN], requireFields: ["nearbyTradesOrPublic"] },
        no: {
          hazardsOff: [PLANT_PEDESTRIAN],
          scopeLine: "Dumper operating area fully segregated from pedestrians and other trades during the works.",
        },
      },
      notSure: { hazardsOn: [PLANT_PEDESTRIAN], failCheck: "Pedestrian interface in the dumper area confirmed" },
    },
    {
      key: "refuelling",
      label: "Will the dumper be refuelled on site?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [REFUELLING_FIRE] },
        no: {
          hazardsOff: [REFUELLING_FIRE],
          substancesOff: ["Diesel fuel and hydraulic oil"],
          emergencyOff: ["Fuel spill or fire"],
          sequenceOff: [7],
          scopeLine: "No on-site refuelling — the machine is refuelled off site, so site refuelling controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: [REFUELLING_FIRE], failCheck: "On-site refuelling confirmed" },
    },
    {
      key: "manualHandling",
      label: "Will operatives handle materials by hand around the dumper (coupling, clearing the skip)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [MANUAL_HANDLING] },
        no: {
          hazardsOff: [MANUAL_HANDLING],
          scopeLine: "No significant manual handling around the machine — loading and movement are mechanised throughout.",
        },
      },
    },
    {
      key: "seatBelt",
      label: "Is seat-belt use mandatory and a working ROPS confirmed at pre-start?",
      kind: "yesno",
      effects: {
        yes: {
          scopeLine: "Seat belt mandatory at all times the machine is in use and ROPS confirmed serviceable at daily pre-start.",
        },
        no: { failCheck: "Mandatory seat-belt use and serviceable ROPS confirmed" },
      },
      notSure: { failCheck: "Mandatory seat-belt use and serviceable ROPS confirmed" },
    },
  ],

  "site-traffic-management": [
    {
      key: "walkwayInspection",
      label: "Who owns and inspects the pedestrian segregation and walkways?",
      kind: "text",
      placeholder: "e.g. site manager, weekly walkway/barrier inspection recorded",
      requiredCheck: "Walkway inspection owner named",
      effects: { "*": { scopeLine: "Pedestrian segregation owned and inspected by: {answer}." } },
    },
    {
      key: "segregation",
      label: "How are pedestrians separated from vehicles on site?",
      kind: "choice",
      options: ["Physical barriers between separate routes", "Marked walkways without physical barriers", "Time/area separation only"],
      effects: {
        "Physical barriers between separate routes": { scopeLine: "Pedestrian/vehicle segregation: physically separated routes with barriers, shown on the traffic management plan." },
        "Marked walkways without physical barriers": { scopeLine: "Pedestrian/vehicle segregation: marked walkways without physical barriers — justify why barriers are not practicable and add crossing controls.", failCheck: "Physical segregation reviewed where barriers are not used" },
        "Time/area separation only": { scopeLine: "Pedestrian/vehicle segregation: time/area separation only — barriers preferred; confirm this is adequate for the movements on site.", failCheck: "Physical segregation reviewed where barriers are not used" },
      },
    },
    {
      key: "publicBoundary",
      label: "Does the site interface with the public at the boundary or gate (footway, road, neighbours)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [BOUNDARY_PUBLIC], requireFields: ["nearbyTradesOrPublic"] },
        no: {
          hazardsOff: [BOUNDARY_PUBLIC],
          emergencyOff: ["Incident at the site gate involving the public"],
          scopeLine: "No public interface at the boundary — the site is enclosed with no public footway or road adjacent to vehicle movements.",
        },
      },
      notSure: { hazardsOn: [BOUNDARY_PUBLIC], failCheck: "Public interface at the boundary confirmed" },
    },
    {
      key: "deliveries",
      label: "Are deliveries received through the site gate?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [GATE_DELIVERIES] },
        no: {
          hazardsOff: [GATE_DELIVERIES],
          permitsRemove: ["Delivery booking / permit system controlling vehicle access through the gate"],
          scopeLine: "No deliveries through the gate during this phase — gate-delivery controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: [GATE_DELIVERIES], failCheck: "Delivery / gate arrangements confirmed" },
    },
    {
      key: "junctions",
      label: "Are there junctions or shared vehicle areas where vehicles could collide?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [VEHICLE_COLLISION] },
        no: {
          hazardsOff: [VEHICLE_COLLISION],
          scopeLine: "No junctions or shared vehicle areas — a single defined route avoids vehicle-to-vehicle conflict.",
        },
      },
    },
    {
      key: "reversingDesignedOut",
      label: "Is reversing designed out first (one-way systems, turning circles) before relying on banksmen?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Reversing designed out through one-way systems and turning circles; banksman control used only for the reversing that cannot be eliminated." },
        no: { failCheck: "Reversing designed out before banksman reliance confirmed" },
      },
      notSure: { failCheck: "Reversing designed out before banksman reliance confirmed" },
    },
  ],

  "manual-handling": [
    {
      key: "assessedMaxLoad",
      label: "Assessed maximum load and how it is handled (weight, dimensions, method)",
      kind: "text",
      placeholder: "e.g. 25kg cement bags two-handed at waist height; blockwork on a wheelbarrow",
      requiredCheck: "Assessed maximum load and method recorded",
      effects: { "*": { scopeLine: "Assessed loads and method: {answer}." } },
    },
    {
      key: "mechanicalAids",
      label: "Are mechanical aids provided for the heavy or bulky loads on the job?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [MANUAL_HANDLING], scopeLine: "Mechanical aids provided for heavy or bulky loads — trolleys, pallet trucks, wheelbarrows or a material hoist matched to the load and route." },
        no: {
          hazardsOff: [MANUAL_HANDLING],
          scopeLine: "No heavy or bulky loads requiring mechanical aids — handling is limited to light loads within the guideline weights.",
        },
      },
      notSure: { hazardsOn: [MANUAL_HANDLING], failCheck: "Mechanical aids for heavy/bulky loads confirmed" },
    },
    {
      key: "teamLifts",
      label: "Will any loads be handled by team (two-person) lifts?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [TEAM_LIFT], scopeLine: "Team lifts used for heavy or awkward items — one person directs, the team is matched for capability and the route is planned and clear." },
        no: {
          hazardsOff: [TEAM_LIFT],
          sequenceOff: [5],
          scopeLine: "No team lifts — loads are broken down or mechanised so single-person handling stays within the guideline weights.",
        },
      },
      notSure: { hazardsOn: [TEAM_LIFT], failCheck: "Team-lift requirement confirmed" },
    },
    {
      key: "carryRoutes",
      label: "Are loads carried over walking routes (rather than only set down at the delivery point)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [SLIPS_TRIPS], scopeLine: "Loads carried over walking routes — routes kept clear, level and lit, and load size limited so the operative can see ahead." },
        no: {
          hazardsOff: [SLIPS_TRIPS],
          scopeLine: "No carrying over walking routes — loads are handled at the point of delivery, so carry-route slip/trip controls are not required for this task.",
        },
      },
    },
    {
      key: "sharpLoads",
      label: "Are sharp, banded or unstable loads handled (steel, banded packs, glass)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [CUT_CRUSH] },
        no: {
          hazardsOff: [CUT_CRUSH],
          scopeLine: "No sharp, banded or unstable loads — cut/crush controls beyond standard gloves and boots are not required for this task.",
        },
      },
    },
    {
      key: "training",
      label: "Have operatives been given manual handling training, and has a TILE assessment been done for the actual loads?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Operatives trained in manual handling and a TILE assessment completed for the actual loads on the job." },
        no: { failCheck: "Manual handling training and TILE assessment confirmed" },
      },
      notSure: { failCheck: "Manual handling training and TILE assessment confirmed" },
    },
  ],
};
